// =============================================================================
// ui/state/useMultiSource.ts — have two loggers written to one account?
// -----------------------------------------------------------------------------
// ⚠ BOOK-WIDE, and deliberately outside the global date filter. The situation it
// names took six weeks to notice (D148); a warning that went quiet because the
// trader filtered to this week would be worse than none, because the silence
// would read as an all-clear.
//
// ⚠ READ ONCE PER MOUNT of the Accounts tab, not on an interval. Nothing about a
// historical conflict changes while the tab is open, and the situation is fixed
// by turning a logger off — not by waiting.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSupabase } from './supabase';
import { loadMultiSourceTrades } from '../../io/sync/multiSourceRead';
import {
  detectMultiSourceDays, summariseMultiSource,
  type MultiSourceDay, type MultiSourceSummary,
} from '../../selectors/multiSource';

export type MultiSourcePhase = 'loading' | 'ready' | 'failed';

export interface MultiSourceState {
  phase: MultiSourcePhase;
  /**
   * ⚠ NULL until a read SUCCEEDED. An empty array means "no account has two
   * loggers on it", which is the all-clear — and the whole point of keeping the
   * two apart is that a failed check must never render as one.
   */
  days: readonly MultiSourceDay[] | null;
  summary: MultiSourceSummary | null;
  error: string | null;
  reload: () => void;
}

export function useMultiSource(): MultiSourceState {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<MultiSourcePhase>('loading');
  const [days, setDays] = useState<readonly MultiSourceDay[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      const { trades, error: readError } = await loadMultiSourceTrades(sb);
      if (cancelled) return;

      if (readError) {
        // ⚠ `days` stays NULL — never []. The read already reported itself to the
        // activity log (#52); what matters here is that the card can say
        // "couldn't check" rather than showing the all-clear.
        setError(readError);
        setPhase('failed');
        return;
      }

      setDays(detectMultiSourceDays(trades));
      setPhase('ready');
    })();

    return () => { cancelled = true; };
  }, [sb, nonce]);

  const summary = useMemo(
    () => (days ? summariseMultiSource(days) : null),
    [days],
  );

  return useMemo(
    () => ({ phase, days, summary, error, reload }),
    [phase, days, summary, error, reload],
  );
}
